/**
 * InvitationPage — first-impression landing for an invited interviewee.
 *
 * Sprint 11.5 redesign: mirror the CompletePage layout (brand strip + 720px
 * column + footer crumb). Resolves the invitation, renders a brief-style
 * card with eyebrow + headline + description + Mono numbered topic outline,
 * then a Sienna-primary "Start interview" button that navigates the
 * interviewee to `/session/:id` where the real interview UI mounts.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { InterviewSession } from '@shared/types.ts';
import { getSessionByInvitation } from '../services/api';
import { buildGuideFromSession, buildGuideSummary } from '../lib/guideTemplate';
import { KaivaIcon, KaivaWordmark } from './brand';
import { ErrorPill, Skeleton } from './feedback';

interface SessionPreview {
  id: string;
  sessionName: string;
  description: string | null;
  sections: Array<{ id: string; title: string; estimatedMinutes: number }>;
}

export function InvitationPage() {
  const { invitationId } = useParams<{ invitationId: string }>();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<SessionPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const resolveInvitation = useCallback(async () => {
    if (!invitationId) {
      setError('Invitation ID is missing from the URL.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getSessionByInvitation(invitationId);
      const session = response.session as InterviewSession;
      const guide = buildGuideFromSession(session);
      const sectionList = guide
        ? buildGuideSummary(guide).sections.map((s) => ({
            id: s.id,
            title: s.title,
            estimatedMinutes: s.estimatedMinutes,
          }))
        : [];

      const structureData = session.structureData as Record<string, unknown> | undefined;
      const description =
        (typeof structureData?.description === 'string' && structureData.description) || null;

      setPreview({
        id: session.id,
        sessionName: session.sessionName || session.structureName || 'Interview Session',
        description,
        sections: sectionList,
      });
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : 'Failed to load invitation.';
      setError(
        rawMessage.includes('(404)')
          ? 'No interview session found for this invitation link. It may not have been set up yet.'
          : rawMessage,
      );
    } finally {
      setIsLoading(false);
    }
  }, [invitationId]);

  useEffect(() => {
    void resolveInvitation();
  }, [resolveInvitation]);

  const totalMinutes = useMemo(() => {
    if (!preview) return 0;
    return preview.sections.reduce((sum, s) => sum + (s.estimatedMinutes || 0), 0);
  }, [preview]);

  return (
    <main
      className="flex min-h-screen flex-col"
      style={{
        background: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
      }}
    >
      <header
        className="px-4 py-4 sm:px-6"
        style={{ borderBottom: '1px solid hsl(var(--border))' }}
      >
        <div className="mx-auto flex w-full items-center gap-3" style={{ maxWidth: 720 }}>
          <KaivaIcon size={28} />
          <KaivaWordmark size={17} tone="ink" />
          <span
            className="ml-2 kv-eyebrow"
            style={{
              color: 'hsl(var(--muted-foreground))',
            }}
          >
            Workspace · Invitation
          </span>
        </div>
      </header>

      {/* Body — text-heavy invitation, carries the whisper substrate */}
      <div className="relative flex-1">
        <div className="kv-substrate kv-substrate--whisper" aria-hidden="true" />
      <div
        className="relative z-10 mx-auto w-full px-4 sm:px-6"
        style={{ maxWidth: 720, paddingTop: 64, paddingBottom: 32 }}
      >
        {isLoading ? (
          <div className="flex flex-col gap-3" aria-busy="true" aria-live="polite">
            <Skeleton variant="card" />
            <Skeleton variant="card" />
            <Skeleton variant="card" />
          </div>
        ) : !preview ? (
          <ErrorPill
            message={error ?? 'Unable to load this interview.'}
            onRetry={resolveInvitation}
          />
        ) : (
          <InvitationCard
            preview={preview}
            totalMinutes={totalMinutes}
            onStart={() => navigate(`/session/${preview.id}`)}
          />
        )}

        <footer
          className="mt-8 flex items-center justify-between gap-3"
          style={{ paddingTop: 16, borderTop: '1px solid hsl(var(--border))' }}
        >
          <Link
            to="/"
            className="kv-eyebrow transition-colors hover:[color:hsl(var(--sienna-deep))]"
            style={{
              color: 'hsl(var(--muted-foreground))',
              textDecoration: 'none',
            }}
          >
            ← Back to home
          </Link>
        </footer>
      </div>
      </div>
    </main>
  );
}

function InvitationCard({
  preview,
  totalMinutes,
  onStart,
}: {
  preview: SessionPreview;
  totalMinutes: number;
  onStart: () => void;
}) {
  return (
    <section
      className="overflow-hidden"
      style={{
        background: 'hsl(var(--paper))',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid hsl(var(--hairline-2))',
        /* v1.6: no shadows on cards. Lift comes from Paper-on-Linen contrast. */
      }}
    >
      {/* Header — tonal-surface shift instead of 1px divider */}
      <div
        className="px-6 pb-5 pt-6 sm:px-8"
        style={{ background: 'hsl(var(--off-white-container-high))' }}
      >
        <div
          className="mb-1.5 kv-eyebrow kv-eyebrow--accent"
          style={{
            letterSpacing: '0.1em',
          }}
        >
          Interview · Invitation
        </div>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: 'hsl(var(--foreground))',
            letterSpacing: '-0.015em',
            lineHeight: 1.25,
            margin: 0,
          }}
        >
          {preview.sessionName}
        </h1>
        {(preview.sections.length > 0 || totalMinutes > 0) && (
          <p
            className="mt-1.5"
            style={{
              fontSize: 11.5,
              color: 'hsl(var(--muted-foreground))',
              letterSpacing: '0.02em',
            }}
          >
            {[
              preview.sections.length > 0
                ? `${preview.sections.length} section${preview.sections.length === 1 ? '' : 's'}`
                : null,
              totalMinutes > 0 ? `${totalMinutes} min` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col gap-6 px-6 py-6 sm:px-8">
        {preview.description && (
          <p
            style={{
              fontSize: 13.5,
              lineHeight: 1.55,
              letterSpacing: '-0.003em',
              color: 'hsl(var(--foreground))',
              margin: 0,
            }}
          >
            {preview.description}
          </p>
        )}

        {preview.sections.length > 0 && (
          <section>
            <h4
              className="m-0 mb-2 kv-eyebrow"
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: 'hsl(var(--muted-foreground))',
              }}
            >
              What we'll cover
            </h4>
            <ol className="m-0 flex list-none flex-col gap-1.5 p-0">
              {preview.sections.map((section, i) => (
                <li
                  key={section.id}
                  className="flex items-center gap-2.5 rounded-[10px] px-3 py-2"
                  style={{
                    background: 'hsl(var(--muted) / 0.35)',
                    border: '1px solid hsl(var(--border))',
                  }}
                >
                  <span
                    aria-hidden="true"
                    className="inline-grid flex-shrink-0 place-items-center tabular-nums"
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: 'hsl(var(--sienna) / 0.10)',
                      color: 'hsl(var(--sienna-deep))',
                      fontSize: 10.5,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span
                    className="min-w-0 flex-1 truncate"
                    style={{
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: 'hsl(var(--foreground))',
                    }}
                  >
                    {section.title}
                  </span>
                  {section.estimatedMinutes > 0 && (
                    <span
                      className="flex-shrink-0 tabular-nums"
                      style={{
                        fontSize: 10.5,
                        letterSpacing: '0.04em',
                        color: 'hsl(var(--muted-foreground))',
                        textTransform: 'uppercase',
                      }}
                    >
                      {section.estimatedMinutes} min
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>

      {/* Footer — tonal-surface shift instead of 1px divider */}
      <div
        className="flex items-center justify-end gap-2 px-6 py-4 sm:px-8"
        style={{ background: 'hsl(var(--off-white-container-high))' }}
      >
        <button
          type="button"
          onClick={onStart}
          className="inline-flex items-center gap-1.5 transition-colors"
          style={{
            height: 36,
            padding: '0 16px',
            borderRadius: 8,
            background: 'hsl(var(--teal))',
            color: 'hsl(var(--slate-navy))',
            border: '1px solid hsl(var(--teal))',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Start interview
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}

export default InvitationPage;
